import apiRequest from '../config/api'
import { UserProfile } from './user'

export interface PaymentUrlRequest {
  fullname: string
  email: string
  phone_number: string
  courses: { course_id: number; price: number }[]
  total: number
}

export interface PaymentUrlResponse {
  token: string
  redirect_url: string
  message: string
  is_free?: boolean
  url_content?: string
}

// Response types for admin transactions
export interface AdminTransactionCourse {
  id: number
  title: string
  category_id: string
  course_level: string
  max_student: string
  is_public: boolean
  short_description: string
  description: string
  status: string
  type: string
  start_date: string | null
  end_date: string | null
  duration: string | null
  image: string | null
  poster: string | null
  link_ebook: string
  link_group: string
  slug: string | null
  studied: string[]
  price: string
  discount_type: string
  discount: string
  created_at: string
  updated_at: string
}

export interface AdminTransaction {
  id: number
  order_id: string
  course_id: string
  email: string
  fullname: string
  total: string
  notes: string | null
  type: string
  status: string
  created_at: string
  updated_at: string
  course: AdminTransactionCourse
  user: UserProfile
}

export interface AdminTransactionsData {
  current_page: number
  data: AdminTransaction[]
  first_page_url: string
  from: number
  last_page: number
  last_page_url: string
  links: Array<{
    url: string | null
    label: string
    active: boolean
  }>
  next_page_url: string | null
  path: string
  per_page: number
  prev_page_url: string | null
  to: number
  total: number
}

export interface AdminTransactionsResponse {
  message: string
  data: AdminTransactionsData
}

export interface AdminTransactionDetailResponse {
  message: string
  data: AdminTransaction
}

export interface UpdateAdminTransactionRequest {
  order_id: string
  course_id: number
  email: string
  fullname: string
  total: number
  status: string
  notes?: string | null
  type?: string
}

export interface UpdateAdminTransactionResponse {
  message: string
  data: AdminTransaction
}

export interface CreateAdminTransactionRequest {
  order_id: string
  course_id: number
  email: string
  fullname: string
  total: number
  status: string
  notes?: string | null
  type: string
}

export interface CreateAdminTransactionResponse {
  message: string
  data: AdminTransaction
}

// ----- Update Transaction Status (Public) -----
export type OrderStatus = 'paid' | 'pending' | 'failed'

export interface UpdateTransactionStatusRequest {
  transaction_status: string
  order_id: string
}

export interface UpdateTransactionStatusResponse {
  message?: string
  data?: any
  order_id: string
  order_status: OrderStatus
}

/**
 * Update transaction status after payment redirect/notification
 */
export const postUpdateTransactionStatus = async (
  payload: UpdateTransactionStatusRequest,
): Promise<UpdateTransactionStatusResponse> => {
  await apiRequest.getCsrfCookie()
  const res = await apiRequest.post('/api/transactions/update-status', payload)

  // Support either wrapped or flat responses
  const data = res.data?.data || res.data
  return {
    order_id: data.order_id,
    order_status: data.order_status as OrderStatus,
    message: res.data?.message,
    data: res.data?.data,
  }
}

/**
 * Request a payment URL for the given courses.
 * @param data - PaymentUrlRequest with course_id as an array of course IDs.
 * @returns PaymentUrlResponse
 */
export const postPaymentUrl = async (
  data: PaymentUrlRequest,
): Promise<PaymentUrlResponse> => {
  await apiRequest.getCsrfCookie()
  const res = await apiRequest.post('/api/payment_url', data)

  const responseData = res.data.data

  // Handle free content response
  if (responseData.is_free) {
    return {
      token: responseData.token || '',
      redirect_url: responseData.redirect_url || responseData.url_content || '',
      message: res.data.message,
      is_free: responseData.is_free,
      url_content: responseData.url_content,
    }
  }

  // Handle paid content response (existing behavior)
  return {
    token: responseData.token,
    redirect_url: responseData.redirect_url,
    message: res.data.message,
  }
}

export const getAdminTransactions = async (
  params?: Record<string, any>,
): Promise<AdminTransactionsResponse> => {
  await apiRequest.getCsrfCookie()
  const res = await apiRequest.get('/api/admin/transactions', { params })
  return res.data
}

export const getAdminTransactionDetail = async (
  id: number | string,
): Promise<AdminTransactionDetailResponse> => {
  await apiRequest.getCsrfCookie()
  const res = await apiRequest.get(`/api/admin/transactions/${id}`)
  return res.data
}

export const updateAdminTransaction = async (
  id: number | string,
  data: UpdateAdminTransactionRequest,
): Promise<UpdateAdminTransactionResponse> => {
  await apiRequest.getCsrfCookie()
  const res = await apiRequest.put(`/api/admin/transactions/${id}`, data)
  return res.data
}

export const postAdminTransaction = async (
  data: CreateAdminTransactionRequest,
): Promise<CreateAdminTransactionResponse> => {
  await apiRequest.getCsrfCookie()
  const res = await apiRequest.post('/api/admin/transactions', data)
  return res.data
}

// ----- Check Transaction Status -----
export interface CheckTransactionStatusRequest {
  order_id: string
}

export interface CheckTransactionStatusResponse {
  message: string
  data: {
    order_id: string
    status: string
    details: {
      status_code: string
      transaction_id: string
      gross_amount: string
      currency: string
      order_id: string
      payment_type: string
      signature_key: string
      transaction_status: string
      fraud_status: string
      status_message: string
      merchant_id: string
      transaction_time: string
      expiry_time: string
    }
  }
}

/**
 * Check transaction status by order ID
 */
export const checkTransactionStatus = async (
  data: CheckTransactionStatusRequest,
): Promise<CheckTransactionStatusResponse> => {
  await apiRequest.getCsrfCookie()
  const res = await apiRequest.post('/api/transactions/check-status', data)
  return res.data
}
